import { store } from "@/store";
import AnalyticsEvent, { EventSource } from "@/types/AnalyticsEvent.interface";
import Channel from "@/types/Channel.interface";
import Tenant from "@/types/Tenant.interface";
import User from "@/types/User.interface";

export interface GoogleEvent {
    name: string,
    properties: object
}
export interface VueRoute {
    name: string,
    path: string,
    query: object
}

//  Define demo emails
const demoEmails = ['@snapdesk.app', '@messagedesk.com']

// Segment page event names 
const routeMap = {
    'BroadcastsIndex'           : 'BroadcastsPage',
    'Inbox'                     : 'InboxPage',
    'BroadcastsCreate'          : 'BroadcastComposePage',
    'Home'                      : 'HomePage',
    'ContactsIndex'             : 'ContactsPage',
    'GroupsIndex'               : 'GroupsPage',
    'AutoResponders'            : 'AutoRespondersPage',
    'OfficeHours'               : 'OfficerHoursPage',
    'FormsIndex'                : 'FormsPage',
    'ChatbotsIndex'             : 'ChatbotsPage',
    'Teammates'                 : 'TeammatesPage',
    'TemplatesIndex'            : 'TemplatesPage',
    'SettingsIndex'             : 'SettingsPage',
    'Account'                   : 'AccountPage',
    'BillingSubscription'       : 'CurrentSubscriptionPage',
    'BillingSubscriptionForm'   : 'SubscriptionPlansPage',
    'BillingManage'             : 'EditSubscriptionPage',
    'Opt'                       : 'ComplianceSettingsPage',
    'Messages'                  : 'MessageHistoryPage',
    'MessageSignature'          : 'MessageSignaturePage',
    'PluginsIndex'              : 'PluginsPage',
    'ShortLinks'                : 'ShortLinksPage',
    'MediaIndex'                : 'MediaPage',
    'Sphere'                    : 'SpherePage',
    'BrandInfo'                 : 'NumberRegistrationPage',
    'CustomFields'              : 'CustomFieldsPage',
    'PersonsMerge'              : 'MergeContactsPage',
    'FreeCredits'               : 'FreeCreditsPage',
    'Qbo'                       : 'QboConnectionPage',
    'Payments'                  : 'PaymentAutomationsPage',
    'InvoicesIndex'             : 'InvoicesPage',
    'Xero'                      : 'XeroConnectionPage',
    'GroupsCreate'              : 'CreateGroupPage',
    'CsvImport'                 : 'CsvImportPage',
    'NotificationsIndex'        : 'NotifcationsPage',
    'SignIn'                    : 'SignInPage',
    'InvitationsAccept'         : 'AcceptInvitationPage',
    'MediaFolder'               : 'MediaFolderPage',
    'RegisterVerify'            : 'EmailVerifyPage',
    'RegisterVerifySms'         : 'PhoneVerifyPage',
    'RegisterPlan'              : 'ChooseTrialPlanTypePage',
    'RegisterPayment'           : 'AddCardForTrialPage',
    'AccountPasswordReset'      : 'PasswordResetPage', 
    'BroadcastProfile'          : 'BroadcastAnalyticsPage', 
    'BroadcastsEdit'            : 'BroadcastComposePage',
    'ChatbotProfile'            : 'EditChatbotPage',
    'ForgotPassword'            : 'ForgotPasswordPage',
    'FormsProfile'              : 'EditFormPage',
    'Inbox Group'               : 'InboxViewPage',
    'GroupProfile'              : 'SingleGroupPage',
    'GroupsEdit'                : 'EditGroupPage',
    'RegisterUser'              : 'RegisterUserPage'
}

/**
 * Set user properties for UA/GA4
 * 
 * @param {User} user - the auth user
 * @return {void}
 */
// const gaInit = (userId: number): void => {
//     // Grabs our _ga cookie from the window a.k.a client_id
//     window.gtag('get', process.env.VUE_APP_GA_ID, 'client_id', function(clientID) {
//         window.gtag('set', {
//             'client_id': clientID
//         })
        
//         // We use local storage so that we can grab the clientID whenever we identify a user (segment identify)
//         window.localStorage.setItem("clientID", JSON.stringify(clientID));
//     })

//     // Sets properties on our gtag that will be passed with every event that is fired for the user
//     window.gtag('set', {
//         'user_id': userId,
//         'url_base': process.env.VUE_APP_URL_BASE
//     }) 
// }

/**
 * Determine if the user is a demo account.  Ie, has
 * a messagedesk email
 * 
 * @param {User} user 
 * @returns 
 */
const isDemo = (user: User): boolean => {
    //  If the user does NOT exist, return false
    if (!user) return false;

    //  If the user does NOT have an email, return false
    if (!user.email) return false

    for (const demoEmail of demoEmails) {
        //  Check if the admin email is a demo email
        let isDemo = user.email.includes(demoEmail)

        //  If the admin email is a demo, break and return true!
        if (isDemo) return true
    }

    //  Else, we didn't match with a demo email, so return false
    return false
}

/**
 * We use 'identify' to bind the Segment anonymousId (ie Device id)
 * with our tenant admin user id
 * 
 * @param {User} admin - the tenant's admin user
 * @param {Array} data - properties that will be appended to the reporting payload
 * @return {void}
 */
const segmentIdentify = (user: User, data: object = null): void => {
    //  Get the user role
    const role = store.getters.userRole(user)

    //  Get the demo property
    const demo = isDemo(user)

    //  Check if the role is "admin"
    const isAdmin = role && role.name === 'Admin'

    //  Assemble the segment payload
    let payload = {
        client_id: window.localStorage.getItem("clientID"),
        demo: demo,
        user_email: user.email,
        user_email_verified: user.email_verified,
        user_firstname: user.firstname,
        user_id: user.id,
        user_lastname: user.lastname,
        user_mobilephone: user.mobilephone,
        user_mobilephone_verified: user.mobilephone_verified,
        user_profile_picture: user.logo_route_id ? true : false,
        user_role: role
    }

    //  If event data exists, append it the payload
    if (data) payload = { ...payload, ...data };

    //  @ts-ignore If we have a role, append is admin
    if (role) payload = {...payload, is_admin: isAdmin }

    //  Report the admin user to Segment
    try {

    window.analytics.identify(user.id, payload)
    }
    catch (exception) {
        console.log('broke', exception)
    }
}


const segmentInit = (): void => {
    //  Get Segment writeKey from .env
    const snippet = process.env.VUE_APP_SEGMENT_SNIPPET

    //  @ts-ignore - Initialize segment
    !function () {
        var analytics = window.analytics = window.analytics || []; if (!analytics.initialize) if (analytics.invoked) window.console && console.error && console.error("Segment snippet included twice."); else {
            analytics.invoked = !0; analytics.methods = ["trackSubmit", "trackClick", "trackLink", "trackForm", "pageview", "identify", "reset", "group", "track", "ready", "alias", "debug", "page", "once", "off", "on", "addSourceMiddleware", "addIntegrationMiddleware", "setAnonymousId", "addDestinationMiddleware"]; analytics.factory = function (e) { return function () { var t = Array.prototype.slice.call(arguments); t.unshift(e); analytics.push(t); return analytics } }; for (var e = 0; e < analytics.methods.length; e++) { var t = analytics.methods[e]; analytics[t] = analytics.factory(t) } analytics.load = function (e, t) { var n = document.createElement("script"); n.type = "text/javascript"; n.async = !0; n.src = "https://cdn.segment.com/analytics.js/v1/" + e + "/analytics.min.js"; var a = document.getElementsByTagName("script")[0]; a.parentNode.insertBefore(n, a); analytics._loadOptions = t }; analytics.SNIPPET_VERSION = "4.1.0";
            analytics.load(snippet);
        }
    }()
}

export default {
    /**
     * Send an event to UA/GA4
     * 
     * @param {Object} event - the event payload
     * @return {void}
     */
    gaEvent(event: GoogleEvent): void {
        //  Send the event
        window.gtag('event', event.name, {
            ...event.properties,
            'event_category': 'App',
            'event_label': process.env.VUE_APP_URL_BASE,
            'value': 0
        })
    },
    /**
     * Send a page view event to UA/GA4
     * 
     * @param {Object} route - the route payload
     * @return {void}
     */
    gaPageView(route: VueRoute): void {
        //  Report pageview to google analytics
        window.gtag('event', 'page_view', {
            page_title: route.name,
            page_location: `${process.env.VUE_APP_URL_BASE}${route.path}`,
            page_path: route.path
        })
    },

    group: async (data: object = {}): Promise<void> => {
        //  Check if there is a currently set tenant
        const tenant = store.getters.tenant

        //  If not, then don't report
        if (!tenant) return

        //  Assemble the request payload
        const payload = {...data, event_source: EventSource.APP}

        //  Post our Group event to the API
        await store.dispatch('analyticsGroup', payload)
    },
    onApplicationLoaded(): void {
        store.getters.appDebug && console.log('ANALYTICS: onApplicationLoaded')

        //  Check if Segment exists already
        const exists = window.analytics

        //  If it does, then we are done
        if (exists) return

        //  Initialize Segment
        segmentInit()
    },
    //  Handles the Inbox channel assignment selected event
    onInboxAssignmentSelected(channel: Channel, userId: number) {
        //  If the item is 'null' report 'unassigned' to analytics
        if (!userId)
            this.track('ConversationUnassigned', { channel_id: channel.id })
        //  Else, report 'assigned' to analytics
        else this.track('ConversationAssigned', { channel_id: channel.id })
    },
    //  Handles the MultiChannelsClosed event
    onMultiChannelsClosed(channels: Channel[]) {
        //  Get an array of channel ids from out updated channels
        const ids = channels.map((x) => x.id)
        
        //  Track with analytics
        this.track('ConversationCloseToggled', { channel_ids: ids })
    },
    //  Handles the TenantSet event
    onTenantSet(tenant: Tenant): void {
        store.getters.appDebug && console.log('ANALYTICS: onTenantSet', tenant)

        //  Get the currently authenticated user
        const user = store.getters.user

        //  Get the tenant's sms account
        const smsAccount = store.getters.smsAccount

        //  Grab the msisdn from the sms account if it exists
        const number = smsAccount?.msisdn || null

        //  Our payload only exists if we have a number
        const payload = number ? { message_desk_phone_number: number } : null

        //  Use Segment 'Identify' to bind anonymous Id to current user
        segmentIdentify(user, payload)

        //  Capture tenant_id in google analytics (DONT REMOVE AS THIS IS LINKED TO UA AS WELL AS GA4)
        // this.gaEvent({ name: 'tenantSet', properties: { tenant: tenant.id} })

        //  Initialize vitally NPS survey
        // vitallyNPSInit(tenant, user)
    },
    //  Handles the UserLogin event
    onUserLogin(user: User): void {
        store.getters.appDebug && console.log('ANALYTICS: onUserLogin')

        //  Check if Segment exists already
        const exists = window.analytics

        //  If it does NOT, then initialize it
        if (!exists) segmentInit()

        //  Google Analytics user id initialization
        // gaInit(user.id)

        //  Use Segment 'Identify' to bind anonymous Id to current user
        segmentIdentify(user)
    },
    //  Handles the UserRegistered event
    onUserRegistered(user: User, data: object = {}): void {
        //  Google analytics UA/GA4 tracking for user registered event
        // gaInit(user.id)
        
        // (DONT REMOVE AS THIS IS LINKED TO UA AS WELL AS GA4)
        // this.gaEvent({ name: 'User Registration Complete', properties: { user_id: user.id } })

        //  Track in segment
        //  There is no tenant at this point, but we know the user signing up will be the
        //  admin user, so we use 'admin' to bind the user to the Segment anonymous id
        segmentIdentify(user, data)

        //  Send the registration event to Analytics
        this.segmentTrack('RegistrationCompleted', { 
            type: 'Setup Wizard', 
            email: user.email, 
            phone_number: user.mobilephone,
            ...data
        })
    },
    /**
     * Send a page event to Segment - Fires whenever a user loads a page on our app
     * 
     * @param {object} to - An object that contains all the page data that user has moved to
     * @param {object} from -An object that contains all the previous page data that user came from 
     * @return {void}
     */
    segmentPage(to, from): void {
        // Set our eventname based off of route name map
        const eventName = routeMap[to.name]
        
        // If the route isn't in our map we won't send it (prevents data pollution)
        if (!eventName){
            return 
        }
        
        // When a user loads in directly to our inbox from refresh or signin our full path is null, so we make sure we can actually check our full path
        const isInboxPagePathSet = to.name == 'Inbox' && typeof(to.fullPath != 'undefined')
        
        // Checks if we are still on the inbox page or not
        const isInboxPageStill  = to.fullPath != to.path || to.path == from.path
      
        // If we are in the inbox page, we need to ensure the paths match otherwise it means the user is going into an inbox channel and 
        // we don't want to report inbox page for every channel change or change from open to closed
        if (isInboxPagePathSet && isInboxPageStill){
            return 
        }
        
        // Get current user 
        const user = store.getters.user
        
        // We pass user id for every page event otherwise we don't know who viewed the page in June
        const payload = {
            user_id: user ? user.id : null,
            referrer: from.path ? from.path : null,
            path: to.path ? to.path : null
        }

        // Send Segment our page event 
        window.analytics.page(eventName, payload)
    },
    /**
     * Send an event to Segment - we use this when a user is NOT authenticated
     * 
     * @param {String} eventName - the name of the event to be reported (Pascal case)
     * @param {Array} data - data that will be appended to the event payload (optional)
     * @param {User} user - the current user
     * @return {void}
     */
    segmentTrack(eventName: string, data: object = {}): void {
        //  Get the tenant
        const tenant = store.getters.tenant

        //  Get user properties
        const user = store.getters?.user

        //  Get the demo property
        const demo = isDemo(user)

        //  Assemble the standardized event payload
        let payload = {
            demo: demo,
            event_source: 'APP',
            paying_user: tenant && tenant.account_status == 'active'
        }

        //  @ts-ignore We add this to our data object so that every segment event now has a tenant_id property for MCS to do better filtering on June.so
        //  We need to check that our tenant exists first before we try to access it's id, this is because some events sent to segmentTrack have no tenant context
        if (tenant) data.tenant_id = tenant.id || 'No Tenant ID Found'

        //  Append our data to the payload
        payload = { ...payload, ...data}

        //  Report event to Segment
        window.analytics.track(eventName, payload);
    },
    /**
     * Send an POST /analytics to the API.  We use this to bypass
     * client-side segmentTrack, so all analytics reporting is controlled
     * server-side.
     * 
     * @param {string} eventName - the name of the event we're reporting
     * @param {object} data - the payload we attached to the event
     * @return {void}
     */
    track: async (eventName: string, data: object = {}): Promise<void> => {
        /**
         * IMPORTANT: Be careful making any changes in this method since it can
         * directly impact MCS pipelines built around Segment
         */

        //  Check if there is a currently set tenant
        const tenant = store.getters.tenant

        //  If not, then don't report
        if (!tenant) return

        //  Assemble the request payload
        let payload: AnalyticsEvent = {
            event_name: eventName,
            event_source: EventSource.APP
        }

        // @ts-ignore We add this to our data object so that every segment event now has a tenant_id property for MCS to do better filtering on June.so
        data.tenant_id = tenant.id || 'No Tenant ID Found'

        //  Append our data to the payload
        payload = { ...payload, ...data}

        //  Post the event to the API
        await store.dispatch('analyticsPost', payload)
    }
}
